import type { DisciplineSpec, ResolvedFloors } from "@shared/labs/disciplines/types";
import type { FrameworkSkill } from "@shared/labs/three-axis";
import { parseLevel } from "@shared/labs/three-axis";

type LevelKey = "L1" | "L2" | "L3" | "L4" | "L5" | "L6";

function levelKeyFor(n: number): LevelKey | undefined {
  if (n < 1 || n > 6) return undefined;
  return `L${n}` as LevelKey;
}

/** Dots (0–5) the role polygon should plot at `level` for `skill`. Reads as
 *  the per-skill RAMP TARGET at that level — what a fully-realised candidate
 *  at the level would look like — not a strict gate. The per-skill polygon
 *  is still useful as a visual reference for "where the candidate sits versus
 *  the level's idealised shape."
 *
 *  When a `discipline` is provided and `skill.id` is in its `skillIds`, the
 *  lookup delegates to `discipline.requiredDotsAtLevel` — which reads from the
 *  live framework markdown floors (Model B), with sub-table levels derived by
 *  the discipline spec. For skills not in the discipline (e.g., legacy generic
 *  frameworks), fall back to the rubric `(N/5)` annotation in `levelMeta`,
 *  then the legacy `requiredDots` field as a last resort. */
function requiredDotsForLevel(
  skill: FrameworkSkill,
  level: number,
  floors: ResolvedFloors,
  discipline?: DisciplineSpec,
): number {
  if (discipline?.skillIds.includes(skill.id)) {
    return discipline.requiredDotsAtLevel(skill.id, level, floors);
  }
  const key = levelKeyFor(level);
  const metaScore = key ? skill.levelMeta?.[key]?.score : undefined;
  if (typeof metaScore === "number") return Math.max(0, Math.min(5, metaScore));
  if (level === parseLevel(skill.required) && typeof skill.requiredDots === "number") {
    return Math.max(0, Math.min(5, skill.requiredDots));
  }
  return 0;
}

function candidateDotsFor(skill: FrameworkSkill): number {
  if (typeof skill.assignedDots === "number") {
    return Math.max(0, Math.min(5, skill.assignedDots));
  }
  const metaScore = skill.levelMeta?.[skill.assigned]?.score;
  if (typeof metaScore === "number") return Math.max(0, Math.min(5, metaScore));
  return parseLevel(skill.assigned);
}

interface RadarProps {
  skills: FrameworkSkill[];
  /** Level computed by the model for the full profile (rec.appliedLevel). The
   *  radar labels the matching ring as "requisito do papel LX" and flanks it
   *  with plain LX tags at level−1 and level+1, mirroring the three cards in
   *  the ladder below. */
  appliedLevel: 1 | 2 | 3 | 4 | 5 | 6;
  /** Role title (kept on the prop shape for callers that still pass it; the
   *  legend doesn't render it because the hero eyebrow above shows the
   *  discipline. The placeholder em-dash on archived rows without a team
   *  field used to leak in as "★ alvo de —"). */
  roleTitle?: string;
  /** Per-skill hire floors. Resolved by the caller — typically from
   *  `readRecommendationFloors(rec) ?? discipline.floorsConstants`. The
   *  helper handles the legacy `fdeFloors` jsonb key internally so call
   *  sites stay on the canonical field. The radar itself is agnostic about
   *  the source. */
  floors: ResolvedFloors;
  /** Discipline of the position. When provided, the radar uses its
   *  `requiredDotsAtLevel` for any skill ID in `discipline.skillIds`.
   *  Skills not in the discipline (e.g., legacy generic frameworks) fall
   *  back to the rubric `levelMeta` lookup. */
  discipline?: DisciplineSpec;
}

function polarToCartesian(
  cx: number,
  cy: number,
  r: number,
  angleRad: number,
): { x: number; y: number } {
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy - r * Math.sin(angleRad),
  };
}

function pointsString(pts: { x: number; y: number }[]): string {
  return pts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
}

function wrapSkillLabel(name: string, maxLen = 18): string[] {
  if (name.length <= maxLen) return [name];
  const words = name.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const w of words) {
    if (!current) {
      current = w;
      continue;
    }
    if (`${current} ${w}`.length <= maxLen) {
      current = `${current} ${w}`;
    } else {
      lines.push(current);
      current = w;
    }
  }
  if (current) lines.push(current);
  return lines.slice(0, 2);
}

export function Radar({
  skills,
  appliedLevel,
  // biome-ignore lint/correctness/noUnusedFunctionParameters: prop preserved for the public API
  roleTitle,
  floors,
  discipline,
}: RadarProps) {
  const cx = 210;
  const cy = 210;
  // The radar plots proficiency dots on a 0–5 scale. Rings sit at integer dot
  // counts; the center is 0 dots. `R_PER_DOT` (formerly `R_PER_DOT`) is the
  // radial distance between two consecutive integer rings.
  const R_PER_DOT = 34;
  const n = skills.length;

  // angle for each axis: first spoke points UP (π/2 in math convention,
  // since we render y as cy − r·sin(angle)). Going clockwise from there.
  // Keeping a vertex at the top guarantees the L1/L3/L5 labels sit on the
  // exact topmost point of every ring, regardless of how many skills exist.
  const angles = skills.map((_, i) => Math.PI / 2 - (2 * Math.PI * i) / n);

  // Concentric circles for the background grid. Circles (instead of pentagons)
  // remove the "top vertex" artifact — every ring has a smooth tangent at any
  // angle, so an L1/L3/L5 label positioned along any direction sits at a
  // visually identical distance from its ring line. With pentagonal grids,
  // labels pinned to the top spoke landed on a POINT (the vertex), and the
  // actual edge line curved away below them at ~0.73 px/px — making L5 read
  // as "far from the line" relative to L1 at identical pixel offsets.
  const gridCircles = [5, 4, 3, 2, 1].map((dots) => {
    const r = R_PER_DOT * dots;
    return (
      <circle
        key={`grid-${dots}`}
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="#e2e0df"
        strokeWidth="0.8"
        opacity="0.6"
      />
    );
  });

  // Ring labels annotate the 0–5 dot proficiency scale. One small pill per
  // integer dot (1…5) sits on the top spoke, centered on each ring, so the
  // reader can read any vertex's dot count at a glance. The "0 dots" ring
  // is the origin and needs no label.
  const SIMPLE_LABEL_W = 24;
  const SIMPLE_LABEL_H = 13;
  const ringLabels = [1, 2, 3, 4, 5].map((dots) => {
    const r = R_PER_DOT * dots;
    const labelCy = cy - r;
    return (
      <g key={`ring-label-${dots}`}>
        <rect
          x={cx - SIMPLE_LABEL_W / 2}
          y={labelCy - SIMPLE_LABEL_H / 2}
          width={SIMPLE_LABEL_W}
          height={SIMPLE_LABEL_H}
          rx="3.5"
          ry="3.5"
          fill="#FFFFFF"
          stroke="#e2e0df"
          strokeWidth="0.8"
          opacity="0.98"
        />
        <text
          x={cx}
          y={labelCy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={9}
          fill="#737373"
          fontWeight="600"
          fontFamily="Inter, sans-serif"
        >
          {`${dots}/5`}
        </text>
      </g>
    );
  });

  // skill name labels at spoke endpoints (just past L5)
  const LABEL_OFFSET = R_PER_DOT * 5 + 14;
  const skillLabels = skills.map((skill, i) => {
    const angle = angles[i];
    const pt = polarToCartesian(cx, cy, LABEL_OFFSET, angle);
    const cos = Math.cos(angle);
    const anchor: "start" | "middle" | "end" =
      Math.abs(cos) < 0.2 ? "middle" : cos > 0 ? "start" : "end";
    const lines = wrapSkillLabel(skill.name);
    return (
      <g key={`label-${skill.id}`}>
        {lines.map((line, li) => (
          <text
            key={`${skill.id}-line-${line}`}
            x={pt.x.toFixed(2)}
            y={(pt.y + li * 11).toFixed(2)}
            textAnchor={anchor}
            dominantBaseline="middle"
            fontSize="10"
            fill="#737373"
            fontWeight="500"
          >
            {line}
          </text>
        ))}
      </g>
    );
  });

  // spokes — keyed by skill.id for stability
  const spokes = skills.map((skill, i) => {
    const outer = polarToCartesian(cx, cy, R_PER_DOT * 5, angles[i]);
    return (
      <line
        key={`spoke-${skill.id}`}
        x1={cx}
        y1={cy}
        x2={outer.x.toFixed(2)}
        y2={outer.y.toFixed(2)}
        stroke="#e2e0df"
        strokeWidth="0.8"
        opacity="0.6"
      />
    );
  });

  // The role polygon plots the per-skill RAMP TARGET for the applied role
  // level — e.g. at L4, HR Fluency targets 2 dots while Agency to influence
  // stakeholders targets 3 — so vertices can sit on different rings. The
  // neighbor (±1 level) polygons use the same per-skill lookup against the
  // adjacent role level. Under discipline compensation rules the level decision
  // itself runs over core-skill aggregate (max + breadth), so candidates can
  // qualify for a level without hitting every spoke of this polygon — the
  // visual is the level's idealised shape, not a strict gate.
  // When the role polygon coincides with the candidate polygon on every
  // spoke, nudge it radially so the dashed outline stays readable.
  const ROLE_OFFSET = 6;

  const rolePtsForLevel = (level: number) =>
    skills.map((skill, i) => {
      const dots = requiredDotsForLevel(skill, level, floors, discipline);
      const r = R_PER_DOT * dots + (dots > 0 ? ROLE_OFFSET : 0);
      return polarToCartesian(cx, cy, r, angles[i]);
    });

  const rolePts = rolePtsForLevel(appliedLevel);
  const roleMinusPts = rolePtsForLevel(Math.max(1, appliedLevel - 1));
  const rolePlusPts = rolePtsForLevel(Math.min(6, appliedLevel + 1));

  const candidateEntries = skills.map((skill, i) => {
    const dots = candidateDotsFor(skill);
    const r = R_PER_DOT * dots;
    return { id: skill.id, pt: polarToCartesian(cx, cy, r, angles[i]) };
  });
  const candidatePts = candidateEntries.map((e) => e.pt);

  // Legend stacks vertically at the bottom so the role-title + level strings
  // fit without truncation. Three rows at legendY0 .. legendY0 + 2·ROW_H.
  // Each row is centered horizontally around cx: we estimate text width from
  // character count at fontSize 10 sans-serif (~5.6 px/char avg) and place the
  // [icon 24w] + [6 gap] + [text] block so its midpoint equals cx.
  const legendY0 = 420;
  const LEGEND_ROW_H = 15;
  const LEGEND_ICON_W = 24;
  const LEGEND_GAP = 6;
  const LEGEND_CHAR_W = 5.6;
  const legendRowY = (i: number) => legendY0 + i * LEGEND_ROW_H;
  const appliedMinus = Math.max(1, appliedLevel - 1);
  const appliedPlus = Math.min(6, appliedLevel + 1);
  const neighborLevels =
    appliedMinus === appliedLevel
      ? `L${appliedPlus}`
      : appliedPlus === appliedLevel
        ? `L${appliedMinus}`
        : `L${appliedMinus}/L${appliedPlus}`;

  const legendRows = [
    { kind: "dot" as const, label: "proficiência da pessoa (em ★)" },
    {
      kind: "dashed-strong" as const,
      label: `★ alvo no L${appliedLevel} (rampa por skill)`,
    },
    {
      kind: "dashed-faint" as const,
      label: `★ alvo no ${neighborLevels}`,
    },
  ];
  const legendLayout = legendRows.map((row, i) => {
    const textW = row.label.length * LEGEND_CHAR_W;
    const totalW = LEGEND_ICON_W + LEGEND_GAP + textW;
    const startX = cx - totalW / 2;
    return {
      ...row,
      y: legendRowY(i),
      iconStartX: startX,
      iconEndX: startX + LEGEND_ICON_W,
      iconCenterX: startX + LEGEND_ICON_W / 2,
      textX: startX + LEGEND_ICON_W + LEGEND_GAP,
    };
  });

  return (
    <svg
      viewBox="-40 -10 500 470"
      data-testid="skills-radar"
      aria-label="Radar de skills"
      style={{ width: "100%", height: "auto" }}
    >
      {/* background grid */}
      {gridCircles}
      {/* spokes */}
      {spokes}
      {/* candidate polygon — drawn first so the dashed role outline stays
          legible when required === assigned for every skill (polygons overlap) */}
      <polygon
        points={pointsString(candidatePts)}
        fill="rgba(243,53,75,0.14)"
        stroke="#f3354b"
        strokeWidth="2"
      />
      {/* candidate vertex dots */}
      {candidateEntries.map(({ id, pt }) => (
        <circle key={`dot-${id}`} cx={pt.x.toFixed(2)} cy={pt.y.toFixed(2)} r="4" fill="#f3354b" />
      ))}
      {/* role tolerance bands — role-1 (below) and role+1 (above) as faint
          dotted outlines so the target ring reads as the centerpiece while
          still showing the ±1 level corridor for context. Visually distinct
          from the dashed-navy target: a dot pattern (1 4) at lower opacity
          so the eye can't confuse the two at a glance. */}
      <polygon
        points={pointsString(roleMinusPts)}
        fill="none"
        stroke="#737373"
        strokeWidth="1.1"
        strokeDasharray="1 4"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.45"
      />
      <polygon
        points={pointsString(rolePlusPts)}
        fill="none"
        stroke="#737373"
        strokeWidth="1.1"
        strokeDasharray="1 4"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.45"
      />
      {/* role polygon — dashed navy outline on top */}
      <polygon
        points={pointsString(rolePts)}
        fill="none"
        stroke="#262626"
        strokeWidth="1.75"
        strokeDasharray="5 3"
        strokeLinejoin="round"
      />
      {/* role vertex markers — tiny hollow squares on the required-dots ring */}
      {skills.map((skill, i) => {
        const dots = requiredDotsForLevel(skill, appliedLevel, floors, discipline);
        const pt = polarToCartesian(
          cx,
          cy,
          R_PER_DOT * dots + (dots > 0 ? ROLE_OFFSET : 0),
          angles[i],
        );
        return (
          <rect
            key={`role-dot-${skill.id}`}
            x={(pt.x - 3).toFixed(2)}
            y={(pt.y - 3).toFixed(2)}
            width="6"
            height="6"
            fill="#FFFFFF"
            stroke="#262626"
            strokeWidth="1.25"
          />
        );
      })}
      {/* ring level labels (L1, L3, L5) */}
      {ringLabels}
      {/* skill name labels at spoke endpoints */}
      {skillLabels}
      {/* legend — stacked so role title + level fit without truncation.
          Each row is centered horizontally around cx via legendLayout. */}
      <g>
        {legendLayout.map((row) => (
          <g key={row.label}>
            {row.kind === "dot" && <circle cx={row.iconCenterX} cy={row.y} r="5" fill="#f3354b" />}
            {row.kind === "dashed-strong" && (
              <line
                x1={row.iconStartX}
                y1={row.y}
                x2={row.iconEndX}
                y2={row.y}
                stroke="#262626"
                strokeWidth="1.5"
                strokeDasharray="4 3"
                opacity="0.7"
              />
            )}
            {row.kind === "dashed-faint" && (
              <line
                x1={row.iconStartX}
                y1={row.y}
                x2={row.iconEndX}
                y2={row.y}
                stroke="#737373"
                strokeWidth="1.1"
                strokeDasharray="1 4"
                strokeLinecap="round"
                opacity="0.55"
              />
            )}
            <text x={row.textX} y={row.y} dominantBaseline="middle" fontSize="10" fill="#737373">
              {row.label}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}
